<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p class="text-sm text-muted-foreground">{{ currentDate }}</p>
      <div class="flex items-center gap-2">
        <span class="font-mono text-sm font-medium text-foreground bg-muted px-3 py-1.5 rounded-md">{{ currentTime }}</span>
        <button
          :disabled="loading"
          class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-white text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          @click="refresh"
        >
          <RotateCcw :class="['h-4 w-4', loading ? 'animate-spin' : '']" />
          {{ t('common.refresh') }}
        </button>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <AppStatCard
        :label="t('adminDashboard.totalEmployees')"
        :value="stats.totalEmployees"
        :sub="`${stats.activeEmployees} ${t('adminDashboard.withActiveSessions')}`"
        :icon="Users"
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        :loading="loading"
        class="cursor-pointer"
        @click="$router.push('/admin/employees')"
      />
      <AppStatCard
        :label="t('adminDashboard.presentToday')"
        :value="stats.todayAttendance"
        :sub="attendanceRate ? `${attendanceRate}% ${t('adminDashboard.attendanceRate')}` : t('common.noData')"
        :icon="CheckCircle"
        iconBg="bg-emerald-50"
        iconColor="text-emerald-600"
        :loading="loading"
        class="cursor-pointer"
        @click="$router.push('/admin/attendance')"
      />
      <AppStatCard
        :label="t('adminDashboard.onLeave')"
        :value="stats.leaves?.total ?? 0"
        :sub="`${stats.leaves?.annual ?? 0} ${t('leave.annualLeave')} · ${stats.leaves?.sick ?? 0} ${t('leave.sickLeave')}`"
        :icon="CalendarOff"
        iconBg="bg-amber-50"
        iconColor="text-amber-600"
        :loading="loading"
        class="cursor-pointer"
        @click="$router.push('/admin/leave-management')"
      />
      <AppStatCard
        :label="t('adminDashboard.pendingApprovals')"
        :value="pendingLeaves.length"
        :sub="`${pendingLeaves.length} ${t('adminDashboard.requestsNeedReview')}`"
        :icon="ClipboardList"
        iconBg="bg-rose-50"
        iconColor="text-rose-600"
        :loading="loading"
        class="cursor-pointer"
        @click="$router.push('/admin/leave-management')"
      />
    </div>

    <!-- Main content grid -->
    <div class="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">

      <!-- Left column -->
      <div class="flex flex-col gap-4">

        <!-- Pending leave requests -->
        <div class="rounded-lg border border-border bg-white">
          <div class="flex items-center justify-between px-4 py-3 border-b border-border">
            <div class="flex items-center gap-2">
              <ClipboardList class="h-4 w-4 text-muted-foreground" />
              <span class="text-sm font-semibold text-foreground">{{ t('adminDashboard.pendingLeaves') }}</span>
              <span v-if="pendingLeaves.length" class="rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-xs font-medium">
                {{ pendingLeaves.length }}
              </span>
            </div>
            <button
              class="text-xs font-medium text-primary hover:underline"
              @click="$router.push('/admin/leave-management')"
            >
              {{ t('adminDashboard.viewAll') }} →
            </button>
          </div>

          <!-- Loading skeleton -->
          <div v-if="loading" class="divide-y divide-border">
            <div v-for="n in 3" :key="n" class="flex items-center gap-3 px-4 py-3">
              <AppSkeleton class="h-8 w-8 rounded-full" />
              <div class="flex-1 space-y-1.5">
                <AppSkeleton class="h-3 w-36" />
                <AppSkeleton class="h-3 w-24" />
              </div>
              <AppSkeleton class="h-7 w-20" />
            </div>
          </div>

          <!-- Empty state -->
          <div v-else-if="!pendingLeaves.length" class="flex flex-col items-center justify-center py-10 text-center px-4">
            <CheckCircle class="h-8 w-8 text-emerald-400 mb-2" />
            <p class="text-sm font-medium text-foreground">{{ t('empDashboard.allCaughtUp') }}</p>
            <p class="text-xs text-muted-foreground mt-0.5">{{ t('adminLeave.noLeaves') }}</p>
          </div>

          <!-- Requests list -->
          <div v-else class="divide-y divide-border">
            <div
              v-for="req in pendingLeaves.slice(0, 5)"
              :key="req.id"
              class="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-medium text-foreground">{{ req.employeeName }}</span>
                  <AppBadge :variant="leaveTypeBadge(req.leaveType)">{{ req.leaveType }}</AppBadge>
                  <span v-if="req.department" class="text-xs text-muted-foreground">· {{ req.department }}</span>
                </div>
                <p class="text-xs text-muted-foreground mt-0.5">
                  <template v-if="req.isShortLeave">
                    {{ formatTimeRange(req.startDate, req.endDate) }}
                  </template>
                  <template v-else>
                    {{ formatDateRange(req.startDate, req.endDate) }}
                    <span v-if="req.days"> · {{ req.days }} {{ t('common.days') }}</span>
                  </template>
                </p>
                <p v-if="req.reason" class="text-xs text-muted-foreground mt-0.5 truncate max-w-xs italic">"{{ req.reason }}"</p>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <button
                  :disabled="approving[req.id]"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  @click="approveLeave(req.id)"
                >
                  <Check class="h-3.5 w-3.5" />
                  {{ t('common.approve') }}
                </button>
                <button
                  :disabled="approving[req.id]"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-white text-xs font-medium text-foreground hover:bg-red-50 hover:border-red-200 hover:text-red-600 disabled:opacity-50 transition-colors"
                  @click="rejectLeave(req.id)"
                >
                  <X class="h-3.5 w-3.5" />
                  {{ t('common.reject') }}
                </button>
              </div>
            </div>
            <div v-if="pendingLeaves.length > 5" class="px-4 py-2 text-center">
              <button class="text-xs text-primary hover:underline" @click="$router.push('/admin/leave-management')">
                +{{ pendingLeaves.length - 5 }} {{ t('common.showMore') }} — {{ t('adminDashboard.viewAll') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Today's attendance breakdown -->
        <div class="rounded-lg border border-border bg-white p-4">
          <div class="flex items-center gap-2 mb-4">
            <CalendarCheck class="h-4 w-4 text-muted-foreground" />
            <span class="text-sm font-semibold text-foreground">{{ t('adminDashboard.todayAttendance') }}</span>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div class="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center">
              <p class="text-2xl font-bold text-emerald-700 tabular-nums">
                <AppSkeleton v-if="loading" class="h-8 w-12 mx-auto" />
                <template v-else>{{ stats.todayAttendance }}</template>
              </p>
              <p class="text-xs font-medium text-emerald-600 mt-1">{{ t('adminDashboard.presentToday') }}</p>
            </div>
            <div class="rounded-lg bg-red-50 border border-red-100 p-3 text-center">
              <p class="text-2xl font-bold text-red-700 tabular-nums">
                <AppSkeleton v-if="loading" class="h-8 w-12 mx-auto" />
                <template v-else>{{ absentCount }}</template>
              </p>
              <p class="text-xs font-medium text-red-600 mt-1">{{ t('adminDashboard.absentToday') }}</p>
            </div>
            <div class="rounded-lg bg-amber-50 border border-amber-100 p-3 text-center">
              <p class="text-2xl font-bold text-amber-700 tabular-nums">
                <AppSkeleton v-if="loading" class="h-8 w-12 mx-auto" />
                <template v-else>{{ stats.attendance?.late ?? 0 }}</template>
              </p>
              <p class="text-xs font-medium text-amber-600 mt-1">{{ t('adminDashboard.lateToday') }}</p>
            </div>
            <div class="rounded-lg bg-blue-50 border border-blue-100 p-3 text-center">
              <p class="text-2xl font-bold text-blue-700 tabular-nums">
                <AppSkeleton v-if="loading" class="h-8 w-12 mx-auto" />
                <template v-else>{{ stats.leaves?.total ?? 0 }}</template>
              </p>
              <p class="text-xs font-medium text-blue-600 mt-1">{{ t('adminDashboard.onLeave') }}</p>
            </div>
          </div>

          <!-- Attendance rate bar -->
          <div v-if="!loading && stats.totalEmployees > 0" class="mt-4">
            <div class="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>{{ t('adminDashboard.attendanceRate') }}</span>
              <span class="font-medium text-foreground">{{ attendanceRate }}%</span>
            </div>
            <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="attendanceRate >= 80 ? 'bg-emerald-500' : attendanceRate >= 60 ? 'bg-amber-500' : 'bg-red-500'"
                :style="{ width: `${attendanceRate}%` }"
              />
            </div>
          </div>
        </div>

        <!-- Recent activity -->
        <div class="rounded-lg border border-border bg-white p-4">
          <div class="flex items-center gap-2 mb-3">
            <Activity class="h-4 w-4 text-muted-foreground" />
            <span class="text-sm font-semibold text-foreground">{{ t('adminDashboard.recentActivity') }}</span>
          </div>

          <div v-if="loading" class="space-y-2">
            <div v-for="n in 4" :key="n" class="flex items-center gap-3">
              <AppSkeleton class="h-2 w-2 rounded-full shrink-0" />
              <AppSkeleton class="h-3 flex-1" />
              <AppSkeleton class="h-3 w-12" />
            </div>
          </div>

          <div v-else-if="recentActivities.length" class="space-y-2">
            <div
              v-for="activity in recentActivities"
              :key="activity.id"
              class="flex items-center gap-3 text-sm"
            >
              <div :class="['h-2 w-2 rounded-full shrink-0', actionDot(activity.action)]" />
              <span class="text-foreground flex-1 min-w-0 truncate">
                <span class="font-medium">{{ activity.employee?.name }}</span>
                {{ ' ' + formatAction(activity.action) }}
              </span>
              <span class="text-muted-foreground text-xs shrink-0">{{ formatRelativeTime(activity.timestamp) }}</span>
            </div>
          </div>

          <p v-else class="text-sm text-muted-foreground">{{ t('adminDashboard.noActivity') }}</p>
        </div>
      </div>

      <!-- Right column: Quick actions -->
      <div class="flex flex-col gap-4">

        <!-- Quick actions -->
        <div class="rounded-lg border border-border bg-white p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{{ t('dashboard.quickActions') }}</p>
          <div class="space-y-2">
            <button
              class="w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border border-border"
              @click="$router.push('/admin/attendance')"
            >
              <CalendarCheck class="h-4 w-4 text-blue-600 shrink-0" />
              {{ t('nav.attendance') }}
            </button>
            <button
              class="w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border border-border"
              @click="$router.push('/admin/leave-management')"
            >
              <ClipboardList class="h-4 w-4 text-amber-600 shrink-0" />
              {{ t('nav.leaveManagementAdmin') }}
            </button>
            <button
              class="w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border border-border"
              @click="$router.push('/admin/employees')"
            >
              <Users class="h-4 w-4 text-emerald-600 shrink-0" />
              {{ t('nav.employees') }}
            </button>
            <button
              class="w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border border-border"
              @click="$router.push('/admin/overtime-management')"
            >
              <Clock class="h-4 w-4 text-purple-600 shrink-0" />
              {{ t('nav.overtimeManagement') }}
            </button>
            <button
              class="w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border border-border"
              @click="$router.push('/admin/payroll')"
            >
              <Banknote class="h-4 w-4 text-rose-600 shrink-0" />
              {{ t('nav.payroll') }}
            </button>
            <button
              class="w-full flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors border border-border"
              @click="$router.push('/admin/reports')"
            >
              <BarChart3 class="h-4 w-4 text-zinc-600 shrink-0" />
              {{ t('reports.generateReport') }}
            </button>
          </div>
        </div>

        <!-- Workforce snapshot -->
        <div class="rounded-lg border border-border bg-white p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{{ t('adminDashboard.workforceSnapshot') }}</p>
          <div class="space-y-3">
            <div v-for="row in workforceRows" :key="row.label" class="flex items-center justify-between text-sm">
              <div class="flex items-center gap-2">
                <div :class="['h-2.5 w-2.5 rounded-full shrink-0', row.dot]" />
                <span class="text-muted-foreground">{{ row.label }}</span>
              </div>
              <span v-if="!loading" class="font-semibold text-foreground tabular-nums">{{ row.value }}</span>
              <AppSkeleton v-else class="h-4 w-8" />
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import adminDashboardService from '@/services/adminDashboardService'
import { adminService } from '@/services/adminService'
import {
  RotateCcw, Users, CheckCircle, CalendarOff, ClipboardList,
  CalendarCheck, Activity, Clock, BarChart3, Banknote,
  Check, X,
} from 'lucide-vue-next'
import { AppStatCard, AppBadge, AppSkeleton } from '@/components/ui'

const { t } = useI18n()
const router = useRouter()

const loading      = ref(false)
const currentTime  = ref('')
const currentDate  = ref('')
const approving    = ref({})

const stats = ref({
  totalEmployees: 0,
  activeEmployees: 0,
  todayAttendance: 0,
  attendance: { late: 0 },
  leaves: { total: 0, annual: 0, sick: 0, unpaid: 0 },
})
const pendingLeaves     = ref([])
const recentActivities  = ref([])

let timeTimer = null

const attendanceRate = computed(() => {
  if (!stats.value.totalEmployees) return 0
  return Math.round((stats.value.todayAttendance / stats.value.totalEmployees) * 100)
})

const absentCount = computed(() => {
  const total = stats.value.totalEmployees || 0
  const present = stats.value.todayAttendance || 0
  const onLeave = stats.value.leaves?.total || 0
  return Math.max(0, total - present - onLeave)
})

const workforceRows = computed(() => [
  { label: t('adminDashboard.totalEmployees'),  dot: 'bg-blue-500',   value: stats.value.totalEmployees },
  { label: t('adminDashboard.presentToday'),    dot: 'bg-emerald-500', value: stats.value.todayAttendance },
  { label: t('adminDashboard.onLeave'),         dot: 'bg-amber-500',   value: stats.value.leaves?.total ?? 0 },
  { label: t('adminDashboard.lateToday'),       dot: 'bg-red-400',     value: stats.value.attendance?.late ?? 0 },
  { label: t('adminDashboard.activeSessions'),  dot: 'bg-purple-500',  value: stats.value.activeEmployees },
])

const fetchStats = async () => {
  const r = await adminDashboardService.getDashboardStats()
  if (r?.success && r?.data) {
    stats.value = {
      ...stats.value,
      ...r.data,
      attendance: r.data.attendance || { late: 0 },
      leaves: r.data.leaves || { total: 0, annual: 0, sick: 0, unpaid: 0 },
    }
  }
}

const fetchPendingLeaves = async () => {
  try {
    const r = await adminService.getLeaveRequests({ status: 'pending', limit: 10 })
    const items = r?.requests ?? []
    pendingLeaves.value = items.map(item => ({
      id: item.id,
      employeeName: item.employeeName ?? item.employee_name ?? item.employee?.name ?? '—',
      leaveType: item.leave_type ?? item.leaveType ?? item.type_name ?? 'Leave',
      department: item.department ?? item.department_name ?? '',
      startDate: item.start_date ?? item.startDate,
      endDate: item.end_date ?? item.endDate,
      days: item.days ?? item.total_days ?? null,
      isShortLeave: item.is_short_leave ?? item.isShortLeave ?? false,
      durationHours: item.duration_hours ?? item.durationHours ?? null,
      durationMinutes: item.duration_minutes ?? item.durationMinutes ?? null,
      reason: item.reason ?? '',
    }))
  } catch {
    pendingLeaves.value = []
  }
}

const fetchActivities = async () => {
  const r = await adminDashboardService.getDashboardActivities('4h', 8)
  if (r?.success && r?.data?.recentAttendance) recentActivities.value = r.data.recentAttendance.slice(0, 8)
}

const refresh = async () => {
  loading.value = true
  await Promise.all([fetchStats(), fetchPendingLeaves(), fetchActivities()])
  loading.value = false
}

const approveLeave = async (id) => {
  approving.value[id] = true
  try {
    await adminService.approveLeaveRequest(id)
    pendingLeaves.value = pendingLeaves.value.filter(r => r.id !== id)
    stats.value = { ...stats.value }
  } catch {}
  delete approving.value[id]
}

const rejectLeave = async (id) => {
  approving.value[id] = true
  try {
    await adminService.rejectLeaveRequest(id)
    pendingLeaves.value = pendingLeaves.value.filter(r => r.id !== id)
  } catch {}
  delete approving.value[id]
}

const leaveTypeBadge = (type) => {
  const t = (type || '').toLowerCase()
  if (t.includes('sick')) return 'destructive'
  if (t.includes('annual') || t.includes('vacation')) return 'success'
  if (t.includes('unpaid')) return 'warning'
  return 'default'
}

const formatDateRange = (start, end) => {
  if (!start) return '—'
  const fmt = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (!end || start === end) return fmt(start)
  return `${fmt(start)} – ${fmt(end)}`
}

const formatTimeRange = (start, end) => {
  if (!start) return '—'
  const fmtTime = d => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const fmtDay = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (!end) return `${fmtDay(start)}, ${fmtTime(start)}`
  return `${fmtDay(start)}, ${fmtTime(start)} – ${fmtTime(end)}`
}

const actionDot = action => ({
  clock_in:        'bg-emerald-500',
  clock_out:       'bg-red-400',
  break_start:     'bg-amber-500',
  break_end:       'bg-amber-400',
  location_update: 'bg-blue-500',
}[action] ?? 'bg-zinc-400')

const formatAction = action => ({
  clock_in: t('adminDashboard.actionClockIn'),
  clock_out: t('adminDashboard.actionClockOut'),
  break_start: t('adminDashboard.actionBreakStart'),
  break_end: t('adminDashboard.actionBreakEnd'),
  location_update: t('adminDashboard.actionLocationUpdate'),
}[action] ?? action)

const formatRelativeTime = ts => {
  if (!ts) return ''
  const diff = Date.now() - new Date(ts)
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  return `${Math.floor(min / 60)}h ago`
}

const updateClock = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  currentDate.value = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
}

onMounted(async () => {
  updateClock()
  timeTimer = setInterval(updateClock, 1000)
  await refresh()
})

onUnmounted(() => clearInterval(timeTimer))
</script>
